/**
 * Properties schema engine (Faz 5.5/5.6/5.7) — the OCS schema-driven
 * properties palette, adapted to yap's Entity data-bag model.
 *
 * buildSections() turns the current selection into PropSection[] rows that
 * the PropertiesPanel (and the QuickProperties mini panel) render. Every
 * editable row's commit closure routes through the context's `commit()`,
 * which applies the same patch across ALL selected entities as ONE undo
 * entry (undoablePatch) — a 500-selection bulk edit is a single U.
 *
 * The engine applies only to `cad:*` entities (2D drafting citizens); BIM
 * elements and Sketch solids keep their dedicated panel branches. Computed
 * geometry values (length/area/angles) derive from entity.data, with closed
 * areas computed over entityToPolyline output (shoelace, bulge-expanded).
 */

import type { Entity, Layer } from '../../../state/store';
import { entityToPolyline } from '../../../core/commands/runners/lib';
import { COLOR_BYLAYER } from '../../../components/cad/aciColor';

/** Above this aggregate the panel shows only a numeric summary (OCS
 *  MAX_PROP_AGGREGATE parity — bulk editing stays on the ribbon tools). */
export const MAX_PROP_AGGREGATE = 2000;

/** Multi-selection marker for fields whose values differ across entities. */
export const VARIES = '*VARIES*';

export type PropValue =
    | { kind: 'editText'; value: string; commit: (v: string) => void; multiline?: boolean }
    | { kind: 'readOnly'; text: string; tooltip?: string }
    | {
        kind: 'coordinate';
        axes: { axis: 'x' | 'y' | 'z'; value: number | null; commit: (v: number | null) => void }[];
    }
    | { kind: 'layerChoice'; value: string | null; varies: boolean; commit: (layerId: string) => void }
    | { kind: 'colorChoice'; value: string; varies: boolean; commit: (color: string) => void }
    | { kind: 'linetypeChoice'; value: string; varies: boolean; commit: (lt: string) => void }
    | { kind: 'lwChoice'; value: number | 'default'; varies: boolean; commit: (lw: number | 'default') => void }
    | { kind: 'transparencyEdit'; value: number | null; varies: boolean; commit: (t: number | null) => void }
    | { kind: 'boolToggle'; value: boolean; varies: boolean; commit: (b: boolean) => void }
    | { kind: 'numberInput'; value: number; varies: boolean; min?: number; commit: (n: number) => void }
    | { kind: 'stepper'; index: number; count: number; onIndex: (i: number) => void }
    | { kind: 'hyperlink'; text: string; url: string };

export interface PropRow {
    /** Stable row id for React keys. */
    id: string;
    /** i18n key for the row label. */
    labelKey: string;
    /** Literal label override (attribute tags render their own tag name). */
    labelOverride?: string;
    value: PropValue;
}

export interface PropSection {
    id: string;
    /** i18n key for the collapsible section title. */
    titleKey: string;
    rows: PropRow[];
}

export interface SchemaContext {
    /** Apply one patch across every selected entity as ONE undo entry. */
    commit: (patch: (entity: Entity) => Partial<Entity> | null) => void;
    /** Current LWPOLYLINE vertex under the stepper (single selection). */
    vertexIndex: number;
    setVertexIndex: (i: number) => void;
    layers: Record<string, Layer>;
}

// ---------------------------------------------------------------------------
// Geometry math helpers (data-bag driven; mirrors the render/runners math)
// ---------------------------------------------------------------------------

type Bag = Record<string, any>;

function pt(v: unknown): [number, number, number] {
    return Array.isArray(v) ? [Number(v[0]) || 0, Number(v[1]) || 0, Number(v[2]) || 0] : [0, 0, 0];
}

const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;

/** Shoelace area of a closed ring (already bulge-expanded by the resolver). */
function ringArea(points: [number, number, number][]): number {
    let a = 0;
    for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const q = points[(i + 1) % points.length];
        a += p[0] * q[1] - q[0] * p[1];
    }
    return Math.abs(a) / 2;
}

function ringLength(points: [number, number, number][], closed: boolean): number {
    let len = 0;
    for (let i = 0; i < points.length - 1; i++) {
        len += Math.hypot(points[i + 1][0] - points[i][0], points[i + 1][1] - points[i][1]);
    }
    if (closed && points.length > 2) {
        len += Math.hypot(points[0][0] - points[points.length - 1][0], points[0][1] - points[points.length - 1][1]);
    }
    return len;
}

/** Circular segment area of an arc (r²/2·(θ − sin θ)). */
function arcArea(radius: number, sweep: number): number {
    return (radius * radius / 2) * (sweep - Math.sin(sweep));
}

// ---------------------------------------------------------------------------
// Value resolution across the selection
// ---------------------------------------------------------------------------

function allSame<T>(entities: Entity[], read: (e: Entity) => T): { value: T; varies: boolean } {
    if (entities.length === 0) return { value: undefined as unknown as T, varies: false };
    const first = read(entities[0]);
    for (let i = 1; i < entities.length; i++) {
        if (read(entities[i]) !== first) return { value: first, varies: true };
    }
    return { value: first, varies: false };
}

/** Set one data-bag key on every entity (top-level fields ride the patch). */
function setData(key: string, value: unknown) {
    return (e: Entity): Partial<Entity> => ({ data: { ...(e.data as Bag), [key]: value } });
}

// ---------------------------------------------------------------------------
// Section builders
// ---------------------------------------------------------------------------

function generalSection(entities: Entity[], ctx: SchemaContext): PropSection {
    const rows: PropRow[] = [];

    // Color (data.color, BYLAYER sentinel when unset) — OCS order: Color first.
    // The VARIES marker compares EFFECTIVE colors (override, else the layer's
    // color — the same resolution entityLineStyle paints with), so three
    // BYLAYER lines on differently colored layers read *VARIES* exactly like
    // OCS ColorVaries.
    const color = allSame(entities, (e) => {
        const c = (e.data as Bag).color as string | undefined;
        return c ?? COLOR_BYLAYER;
    });
    const effColorOf = (e: Entity): string => {
        const c = (e.data as Bag).color as string | undefined;
        if (c) return c;
        return ctx.layers[e.layer]?.color ?? COLOR_BYLAYER;
    };
    const colorVaries = color.varies || allSame(entities, effColorOf).varies;
    rows.push({
        id: 'general.color',
        labelKey: 'ui.cad.props.color',
        value: {
            kind: 'colorChoice',
            value: color.value,
            varies: colorVaries,
            commit: (next) => ctx.commit(setData('color', next === COLOR_BYLAYER ? undefined : next)),
        },
    });

    // Layer (entity.layer).
    const layer = allSame(entities, (e) => e.layer);
    rows.push({
        id: 'general.layer',
        labelKey: 'ui.cad.props.layer',
        value: {
            kind: 'layerChoice',
            value: layer.value ?? null,
            varies: layer.varies,
            commit: (layerId) => ctx.commit(() => ({ layer: layerId })),
        },
    });

    // Linetype (data.linetype, BYLAYER sentinel when unset).
    const lt = allSame(entities, (e) => {
        const v = (e.data as Bag).linetype as string | undefined;
        return v && v !== 'BYLAYER' ? v : 'BYLAYER';
    });
    rows.push({
        id: 'general.linetype',
        labelKey: 'ui.cad.props.linetype',
        value: {
            kind: 'linetypeChoice',
            value: lt.value,
            varies: lt.varies,
            commit: (next) => ctx.commit(setData('linetype', next === 'BYLAYER' ? undefined : next)),
        },
    });

    // Linetype scale (OCS "Linetype scale"; data.linetypeScale, default 1).
    const lts = allSame(entities, (e) => {
        const v = (e.data as Bag).linetypeScale;
        return typeof v === 'number' && v > 0 ? v : 1;
    });
    rows.push({
        id: 'general.linetypeScale',
        labelKey: 'ui.cad.props.linetypeScale',
        value: {
            kind: 'numberInput',
            value: lts.value,
            varies: lts.varies,
            min: 0.01,
            commit: (n) => ctx.commit(setData('linetypeScale', n > 0 ? n : undefined)),
        },
    });

    // Plot style — read-only in the CTB (color-dependent) mode yap renders.
    rows.push({
        id: 'general.plotStyle',
        labelKey: 'ui.cad.props.plotStyle',
        value: { kind: 'readOnly', text: PLOT_STYLE_TEXT, tooltip: 'ui.cad.props.plotStyleNote' },
    });

    // Lineweight (data.lineWeight; 'default' sentinel = BYLAYER).
    const lw = allSame(entities, (e) => {
        const v = (e.data as Bag).lineWeight;
        return typeof v === 'number' && v >= 1 ? v : 'default' as const;
    });
    rows.push({
        id: 'general.lineweight',
        labelKey: 'ui.cad.props.lineweight',
        value: {
            kind: 'lwChoice',
            value: lw.value,
            varies: lw.varies,
            commit: (next) => ctx.commit(setData('lineWeight', next === 'default' ? undefined : next)),
        },
    });

    // Transparency (data.transparency 0-100; null = opaque/ByLayer).
    const tr = allSame(entities, (e) => {
        const v = (e.data as Bag).transparency;
        return typeof v === 'number' ? v : null;
    });
    rows.push({
        id: 'general.transparency',
        labelKey: 'ui.cad.props.transparency',
        value: {
            kind: 'transparencyEdit',
            value: tr.value,
            varies: tr.varies,
            commit: (next) => ctx.commit(setData('transparency', next === null ? undefined : next)),
        },
    });

    return { id: 'general', titleKey: 'ui.cad.props.sectionGeneral', rows };
}

/** Plot style is locked to the CTB ("ByColor") model in this version — the
 *  STB/table-style door stays shut until the plot pipeline grows it. */
const PLOT_STYLE_TEXT = 'ByColor';

/** Coordinate row whose axes commit through one data path. */
function coordRow(
    id: string,
    labelKey: string,
    ctx: SchemaContext,
    entities: Entity[],
    read: (e: Entity) => [number, number, number] | null,
    write: (e: Entity, axis: 'x' | 'y' | 'z', v: number) => Partial<Entity>,
    axes: Array<'x' | 'y' | 'z'> = ['x', 'y', 'z'],
): PropRow {
    const axisIndex: Record<string, number> = { x: 0, y: 1, z: 2 };
    return {
        id,
        labelKey,
        value: {
            kind: 'coordinate',
            axes: axes.map((axis) => {
                const same = allSame(entities, (e) => {
                    const p = read(e);
                    return p ? p[axisIndex[axis]] : null;
                });
                return {
                    axis,
                    value: same.value,
                    commit: (v) => {
                        if (v === null || !Number.isFinite(v)) return;
                        ctx.commit((e) => write(e, axis, v));
                    },
                };
            }),
        },
    };
}

function setPointField(key: string, axis: 'x' | 'y' | 'z', v: number) {
    const idx = { x: 0, y: 1, z: 2 }[axis];
    return (e: Entity): Partial<Entity> => {
        const d = { ...(e.data as Bag) };
        const p = pt(d[key]);
        p[idx] = v;
        d[key] = p;
        return { data: d };
    };
}

// ------------------------------- per-type geometry -------------------------------

function lineSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const e = entities[0];
    const d = e.data as Bag;
    if (!Array.isArray(d.start) || !Array.isArray(d.end)) return null;
    const rows: PropRow[] = [
        coordRow('line.start', 'ui.cad.props.start', ctx, entities, (en) => pt((en.data as Bag).start), (en, ax, v) => setPointField('start', ax, v)(en), ['x', 'y', 'z']),
        coordRow('line.end', 'ui.cad.props.end', ctx, entities, (en) => pt((en.data as Bag).end), (en, ax, v) => setPointField('end', ax, v)(en), ['x', 'y', 'z']),
    ];
    const s = pt(d.start), t = pt(d.end);
    const dx = t[0] - s[0], dy = t[1] - s[1], dz = t[2] - s[2];
    const readOnly: PropRow[] = [
        { id: 'line.dx', labelKey: 'ui.cad.props.deltaX', value: { kind: 'readOnly', text: dx.toFixed(4) } },
        { id: 'line.dy', labelKey: 'ui.cad.props.deltaY', value: { kind: 'readOnly', text: dy.toFixed(4) } },
        { id: 'line.dz', labelKey: 'ui.cad.props.deltaZ', value: { kind: 'readOnly', text: dz.toFixed(4) } },
        { id: 'line.length', labelKey: 'ui.cad.props.length', value: { kind: 'readOnly', text: Math.hypot(dx, dy, dz).toFixed(4) } },
        { id: 'line.angle', labelKey: 'ui.cad.props.angle', value: { kind: 'readOnly', text: `${((Math.atan2(dy, dx) * RAD2DEG + 360) % 360).toFixed(4)}°` } },
    ];
    return { id: 'geometry', titleKey: 'ui.cad.props.sectionGeometry', rows: [...rows, ...readOnly] };
}

function circleSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    if (!Array.isArray(d.center) || typeof d.radius !== 'number') return null;
    const r = d.radius;
    /** Radius-deriving edits (diameter/circumference/area) must keep r > 0. */
    const commitRadius = (v: number) => {
        if (v > 0) ctx.commit(setData('radius', v));
    };
    const varies = allSame(entities, (en) => (en.data as Bag).radius).varies;
    const rows: PropRow[] = [
        coordRow('circle.center', 'ui.cad.props.center', ctx, entities, (en) => pt((en.data as Bag).center), (en, ax, v) => setPointField('center', ax, v)(en)),
        {
            id: 'circle.radius',
            labelKey: 'ui.cad.props.radius',
            value: {
                kind: 'numberInput',
                value: r,
                varies,
                min: 0.0001,
                commit: commitRadius,
            },
        },
        {
            id: 'circle.diameter',
            labelKey: 'ui.cad.props.diameter',
            value: {
                kind: 'numberInput',
                value: r * 2,
                varies,
                commit: (n) => commitRadius(n / 2),
            },
        },
        {
            id: 'circle.circumference',
            labelKey: 'ui.cad.props.circumference',
            value: {
                kind: 'numberInput',
                value: 2 * Math.PI * r,
                varies,
                commit: (n) => commitRadius(n / (2 * Math.PI)),
            },
        },
        {
            id: 'circle.area',
            labelKey: 'ui.cad.props.area',
            value: {
                kind: 'numberInput',
                value: Math.PI * r * r,
                varies,
                commit: (n) => commitRadius(Math.sqrt(Math.max(0, n) / Math.PI)),
            },
        },
    ];
    return { id: 'geometry', titleKey: 'ui.cad.props.sectionGeometry', rows };
}

function arcSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    if (!Array.isArray(d.center) || typeof d.radius !== 'number') return null;
    const r = d.radius;
    const start = typeof d.startAngle === 'number' ? d.startAngle : 0;
    const end = typeof d.endAngle === 'number' ? d.endAngle : Math.PI / 2;
    let sweep = end - start;
    while (sweep <= 0) sweep += Math.PI * 2;
    const setAngle = (key: 'startAngle' | 'endAngle') => (deg: number) =>
        ctx.commit((e): Partial<Entity> => ({ data: { ...(e.data as Bag), [key]: deg * DEG2RAD } }));
    const rows: PropRow[] = [
        coordRow('arc.center', 'ui.cad.props.center', ctx, entities, (en) => pt((en.data as Bag).center), (en, ax, v) => setPointField('center', ax, v)(en)),
        {
            id: 'arc.radius',
            labelKey: 'ui.cad.props.radius',
            value: {
                kind: 'numberInput',
                value: r,
                varies: allSame(entities, (en) => (en.data as Bag).radius).varies,
                min: 0.0001,
                commit: (n) => { if (n > 0) ctx.commit(setData('radius', n)); },
            },
        },
        {
            id: 'arc.startAngle',
            labelKey: 'ui.cad.props.startAngle',
            value: { kind: 'numberInput', value: start * RAD2DEG, varies: allSame(entities, (en) => (en.data as Bag).startAngle).varies, commit: setAngle('startAngle') },
        },
        {
            id: 'arc.endAngle',
            labelKey: 'ui.cad.props.endAngle',
            value: { kind: 'numberInput', value: end * RAD2DEG, varies: allSame(entities, (en) => (en.data as Bag).endAngle).varies, commit: setAngle('endAngle') },
        },
        { id: 'arc.length', labelKey: 'ui.cad.props.arcLength', value: { kind: 'readOnly', text: (r * sweep).toFixed(4) } },
        { id: 'arc.totalAngle', labelKey: 'ui.cad.props.totalAngle', value: { kind: 'readOnly', text: `${(sweep * RAD2DEG).toFixed(4)}°` } },
        { id: 'arc.area', labelKey: 'ui.cad.props.area', value: { kind: 'readOnly', text: arcArea(r, sweep).toFixed(4) } },
    ];
    return { id: 'geometry', titleKey: 'ui.cad.props.sectionGeometry', rows };
}

/** LWPOLYLINE-style vertex editing (polyline; polygon/rectangle keep their
 *  parametric shapes below). Bulge b[i] drives the segment leaving vertex i. */
function polylineSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const e = entities[0];
    const d = e.data as Bag;
    const verts = Array.isArray(d.vertices) ? (d.vertices as number[][]) : null;
    if (!verts || verts.length === 0) return null;
    const closed = !!d.closed;
    const bulges = Array.isArray(d.bulges) ? (d.bulges as number[]) : verts.map(() => 0);
    const idx = Math.min(ctx.vertexIndex, verts.length - 1);

    /** Patch moving vertex[idx] along one axis (returns the data patch; the
     *  caller's commit wraps it into the single undo entry). */
    const vertexPatch = (axis: 'x' | 'y' | 'z', v: number) => (en: Entity): Partial<Entity> => {
        const dd = { ...(en.data as Bag) };
        const list = (dd.vertices as number[][]).map((p) => [...p]);
        const cur = [...(list[idx] ?? [0, 0, 0])];
        cur[{ x: 0, y: 1, z: 2 }[axis]] = v;
        list[idx] = cur;
        dd.vertices = list;
        return { data: dd };
    };

    const expanded = entityToPolyline(e);
    const rows: PropRow[] = [
        {
            id: 'poly.vertex',
            labelKey: 'ui.cad.props.currentVertex',
            value: { kind: 'stepper', index: idx, count: verts.length, onIndex: ctx.setVertexIndex },
        },
        coordRow(
            'poly.vx', 'ui.cad.props.vertexX', ctx, entities,
            () => pt(verts[idx]),
            (_en, ax, v) => vertexPatch(ax, v)(_en),
            ['x', 'y'],
        ),
        {
            id: 'poly.bulge',
            labelKey: 'ui.cad.props.bulge',
            value: {
                kind: 'numberInput',
                value: bulges[idx] ?? 0,
                varies: false,
                commit: (n) => ctx.commit((en): Partial<Entity> => {
                    const dd = { ...(en.data as Bag) };
                    const list = Array.isArray(dd.bulges) ? [...(dd.bulges as number[])] : verts.map(() => 0);
                    list[idx] = n;
                    dd.bulges = list;
                    return { data: dd };
                }),
            },
        },
        {
            id: 'poly.width',
            labelKey: 'ui.cad.props.globalWidth',
            value: {
                kind: 'numberInput',
                value: typeof d.width === 'number' ? d.width : 0,
                varies: allSame(entities, (en) => (en.data as Bag).width).varies,
                min: 0,
                commit: (n) => ctx.commit(setData('width', n > 0 ? n : undefined)),
            },
        },
        {
            id: 'poly.closed',
            labelKey: 'ui.cad.props.closed',
            value: {
                kind: 'boolToggle',
                value: closed,
                varies: allSame(entities, (en) => !!(en.data as Bag).closed).varies,
                commit: (b) => ctx.commit(setData('closed', b)),
            },
        },
        { id: 'poly.length', labelKey: 'ui.cad.props.length', value: { kind: 'readOnly', text: (expanded ? ringLength(expanded, closed) : 0).toFixed(4) } },
        { id: 'poly.area', labelKey: 'ui.cad.props.area', value: { kind: 'readOnly', text: (closed && expanded && expanded.length >= 3 ? ringArea(expanded) : 0).toFixed(4) } },
    ];
    return { id: 'geometry', titleKey: 'ui.cad.props.sectionGeometry', rows };
}

/** Polygon (inscribed) keeps its parametric center/radius/sides model. */
function polygonSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    if (!Array.isArray(d.center) || typeof d.radius !== 'number') return null;
    const sides = Math.max(3, Math.round(Number(d.sides) || 3));
    const rows: PropRow[] = [
        coordRow('polygon.center', 'ui.cad.props.center', ctx, entities, (en) => pt((en.data as Bag).center), (en, ax, v) => setPointField('center', ax, v)(en)),
        {
            id: 'polygon.radius',
            labelKey: 'ui.cad.props.radius',
            value: {
                kind: 'numberInput',
                value: d.radius,
                varies: allSame(entities, (en) => (en.data as Bag).radius).varies,
                min: 0.0001,
                commit: (n) => { if (n > 0) ctx.commit(setData('radius', n)); },
            },
        },
        {
            id: 'polygon.sides',
            labelKey: 'ui.cad.props.sides',
            value: {
                kind: 'numberInput',
                value: sides,
                varies: allSame(entities, (en) => Math.round(Number((en.data as Bag).sides) || 3)).varies,
                min: 3,
                commit: (n) => { if (n >= 3) ctx.commit(setData('sides', Math.round(n))); },
            },
        },
        {
            id: 'polygon.area',
            labelKey: 'ui.cad.props.area',
            value: { kind: 'readOnly', text: (0.5 * sides * d.radius * d.radius * Math.sin((2 * Math.PI) / sides)).toFixed(4) },
        },
    ];
    return { id: 'geometry', titleKey: 'ui.cad.props.sectionGeometry', rows };
}

function rectangleSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    if (!Array.isArray(d.corner1) || !Array.isArray(d.corner2)) return null;
    const c1 = pt(d.corner1), c2 = pt(d.corner2);
    const rows: PropRow[] = [
        coordRow('rect.c1', 'ui.cad.props.corner1', ctx, entities, (en) => pt((en.data as Bag).corner1), (en, ax, v) => setPointField('corner1', ax, v)(en), ['x', 'y']),
        coordRow('rect.c2', 'ui.cad.props.corner2', ctx, entities, (en) => pt((en.data as Bag).corner2), (en, ax, v) => setPointField('corner2', ax, v)(en), ['x', 'y']),
        { id: 'rect.w', labelKey: 'ui.cad.props.width', value: { kind: 'readOnly', text: Math.abs(c2[0] - c1[0]).toFixed(4) } },
        { id: 'rect.h', labelKey: 'ui.cad.props.height', value: { kind: 'readOnly', text: Math.abs(c2[1] - c1[1]).toFixed(4) } },
        { id: 'rect.area', labelKey: 'ui.cad.props.area', value: { kind: 'readOnly', text: (Math.abs(c2[0] - c1[0]) * Math.abs(c2[1] - c1[1])).toFixed(4) } },
    ];
    return { id: 'geometry', titleKey: 'ui.cad.props.sectionGeometry', rows };
}

function insertSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    const rows: PropRow[] = [
        { id: 'insert.name', labelKey: 'ui.cad.props.blockName', value: { kind: 'readOnly', text: String(d.blockName ?? '') } },
        coordRow('insert.pos', 'ui.cad.props.position', ctx, entities, (en) => pt((en.data as Bag).position), (en, ax, v) => setPointField('position', ax, v)(en), ['x', 'y']),
        {
            id: 'insert.scale',
            labelKey: 'ui.cad.props.scale',
            value: {
                kind: 'numberInput',
                value: typeof d.scale === 'number' ? d.scale : 1,
                varies: allSame(entities, (en) => (en.data as Bag).scale).varies,
                min: 0.0001,
                commit: (n) => { if (n > 0) ctx.commit(setData('scale', n)); },
            },
        },
        {
            id: 'insert.rotation',
            labelKey: 'ui.cad.props.rotation',
            value: {
                kind: 'numberInput',
                value: ((typeof d.rotation === 'number' ? d.rotation : 0) * RAD2DEG) % 360,
                varies: allSame(entities, (en) => (en.data as Bag).rotation).varies,
                commit: (n) => ctx.commit(setData('rotation', n * DEG2RAD)),
            },
        },
    ];
    // Attributes table (tag → value, editable per OCS insert schema).
    const values = (d.attributeValues as Record<string, string> | undefined) ?? {};
    for (const [tag, value] of Object.entries(values)) {
        rows.push({
            id: `insert.attr.${tag}`,
            labelKey: 'ui.cad.props.attributeTag',
            labelOverride: tag,
            value: {
                kind: 'editText',
                value: String(value ?? ''),
                commit: (v) => ctx.commit((en): Partial<Entity> => {
                    const dd = { ...(en.data as Bag) };
                    dd.attributeValues = { ...(dd.attributeValues as Record<string, string>), [tag]: v };
                    return { data: dd };
                }),
            },
        });
    }
    return { id: 'geometry', titleKey: 'ui.cad.props.sectionGeometry', rows };
}

function hatchSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    const rows: PropRow[] = [
        { id: 'hatch.pattern', labelKey: 'ui.cad.props.pattern', value: { kind: 'readOnly', text: String(d.pattern ?? 'SOLID') } },
        {
            id: 'hatch.scale',
            labelKey: 'ui.cad.props.scale',
            value: {
                kind: 'numberInput',
                value: typeof d.scale === 'number' ? d.scale : 1,
                varies: allSame(entities, (en) => (en.data as Bag).scale).varies,
                min: 0.0001,
                commit: (n) => { if (n > 0) ctx.commit(setData('scale', n)); },
            },
        },
        {
            id: 'hatch.angle',
            labelKey: 'ui.cad.props.angle',
            value: {
                kind: 'numberInput',
                value: (typeof d.angle === 'number' ? d.angle : 0) * RAD2DEG,
                varies: allSame(entities, (en) => (en.data as Bag).angle).varies,
                commit: (n) => ctx.commit(setData('angle', n * DEG2RAD)),
            },
        },
    ];
    return { id: 'geometry', titleKey: 'ui.cad.props.sectionGeometry', rows };
}

function textSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    const rows: PropRow[] = [
        {
            id: 'text.content',
            labelKey: 'ui.cad.props.content',
            value: {
                kind: 'editText',
                value: String(d.content ?? ''),
                multiline: !!d.multiline,
                commit: (v) => ctx.commit(setData('content', v)),
            },
        },
        {
            id: 'text.height',
            labelKey: 'ui.cad.props.textHeight',
            value: {
                kind: 'numberInput',
                value: typeof d.fontSize === 'number' ? d.fontSize : 0.3,
                varies: allSame(entities, (en) => (en.data as Bag).fontSize).varies,
                min: 0.001,
                commit: (n) => { if (n > 0) ctx.commit(setData('fontSize', n)); },
            },
        },
        {
            id: 'text.rotation',
            labelKey: 'ui.cad.props.rotation',
            value: {
                kind: 'numberInput',
                value: ((typeof d.rotation === 'number' ? d.rotation : 0) * RAD2DEG) % 360,
                varies: allSame(entities, (en) => (en.data as Bag).rotation).varies,
                commit: (n) => ctx.commit(setData('rotation', n * DEG2RAD)),
            },
        },
        coordRow('text.pos', 'ui.cad.props.position', ctx, entities, (en) => pt((en.data as Bag).position), (en, ax, v) => setPointField('position', ax, v)(en), ['x', 'y']),
    ];
    return { id: 'geometry', titleKey: 'ui.cad.props.sectionGeometry', rows };
}

function dimensionSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    const rows: PropRow[] = [
        {
            id: 'dim.override',
            labelKey: 'ui.cad.props.textOverride',
            value: {
                kind: 'editText',
                value: String(d.override ?? ''),
                commit: (v) => ctx.commit(setData('override', v.trim() ? v : undefined)),
            },
        },
        coordRow('dim.textPos', 'ui.cad.props.textPosition', ctx, entities, (en) => pt((en.data as Bag).textPosition), (en, ax, v) => setPointField('textPosition', ax, v)(en), ['x', 'y']),
    ];
    return { id: 'geometry', titleKey: 'ui.cad.props.sectionGeometry', rows };
}

function pointSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    if (!Array.isArray(d.position)) return null;
    return {
        id: 'geometry',
        titleKey: 'ui.cad.props.sectionGeometry',
        rows: [
            coordRow('point.pos', 'ui.cad.props.position', ctx, entities, (en) => pt((en.data as Bag).position), (en, ax, v) => setPointField('position', ax, v)(en)),
        ],
    };
}

function ellipseSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    if (!Array.isArray(d.center)) return null;
    return {
        id: 'geometry',
        titleKey: 'ui.cad.props.sectionGeometry',
        rows: [
            coordRow('ellipse.center', 'ui.cad.props.center', ctx, entities, (en) => pt((en.data as Bag).center), (en, ax, v) => setPointField('center', ax, v)(en)),
            {
                id: 'ellipse.radiusA',
                labelKey: 'ui.cad.props.radiusMajor',
                value: {
                    kind: 'numberInput',
                    value: typeof d.radiusA === 'number' ? d.radiusA : 1,
                    varies: allSame(entities, (en) => (en.data as Bag).radiusA).varies,
                    min: 0.0001,
                    commit: (n) => { if (n > 0) ctx.commit(setData('radiusA', n)); },
                },
            },
            {
                id: 'ellipse.radiusB',
                labelKey: 'ui.cad.props.radiusMinor',
                value: {
                    kind: 'numberInput',
                    value: typeof d.radiusB === 'number' ? d.radiusB : 1,
                    varies: allSame(entities, (en) => (en.data as Bag).radiusB).varies,
                    min: 0.0001,
                    commit: (n) => { if (n > 0) ctx.commit(setData('radiusB', n)); },
                },
            },
            {
                id: 'ellipse.rotation',
                labelKey: 'ui.cad.props.rotation',
                value: {
                    kind: 'numberInput',
                    value: ((typeof d.rotation === 'number' ? d.rotation : 0) * RAD2DEG) % 360,
                    varies: allSame(entities, (en) => (en.data as Bag).rotation).varies,
                    commit: (n) => ctx.commit(setData('rotation', n * DEG2RAD)),
                },
            },
        ],
    };
}

function leaderSection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const d = entities[0].data as Bag;
    return {
        id: 'geometry',
        titleKey: 'ui.cad.props.sectionGeometry',
        rows: [
            {
                id: 'leader.text',
                labelKey: 'ui.cad.props.content',
                value: {
                    kind: 'editText',
                    value: String(d.text ?? ''),
                    commit: (v) => ctx.commit(setData('text', v)),
                },
            },
            coordRow('leader.tip', 'ui.cad.props.tip', ctx, entities, (en) => pt((en.data as Bag).tip), (en, ax, v) => setPointField('tip', ax, v)(en)),
            coordRow('leader.landing', 'ui.cad.props.landing', ctx, entities, (en) => pt((en.data as Bag).landing), (en, ax, v) => setPointField('landing', ax, v)(en)),
        ],
    };
}

/** Per-type geometry section (single selection only — OCS composes the
 *  geometry of the SelectionGroup head, yap keeps the simpler single rule). */
function geometrySection(entities: Entity[], ctx: SchemaContext): PropSection | null {
    const e = entities[0];
    switch (e.type) {
        case 'line': return lineSection(entities, ctx);
        case 'circle': return circleSection(entities, ctx);
        case 'arc': return arcSection(entities, ctx);
        case 'polyline': return polylineSection(entities, ctx);
        case 'polygon': return polygonSection(entities, ctx);
        case 'rectangle': return rectangleSection(entities, ctx);
        case 'insert': case 'block': return insertSection(entities, ctx);
        case 'hatch': return hatchSection(entities, ctx);
        case 'text': case 'mtext': return textSection(entities, ctx);
        case 'dimension': return dimensionSection(entities, ctx);
        case 'point': return pointSection(entities, ctx);
        case 'ellipse': return ellipseSection(entities, ctx);
        case 'leader': return leaderSection(entities, ctx);
        default: return null;
    }
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/** True when the schema engine owns this entity (2D drafting citizens only;
 *  BIM/Sketch branches stay in the panel's own conditional sections). */
export function isCadSchemaEntity(e: Entity): boolean {
    return typeof e.id === 'string' && e.id.startsWith('cad:');
}

/**
 * Build the palette sections for the current selection.
 * - Single selection: General + per-type geometry.
 * - Multi selection: General only, differing fields marked *VARIES*.
 * - Above MAX_PROP_AGGREGATE: a single read-only summary section.
 */
export function buildSections(entities: Entity[], ctx: SchemaContext): PropSection[] {
    if (entities.length === 0) return [];

    if (entities.length > MAX_PROP_AGGREGATE) {
        return [{
            id: 'summary',
            titleKey: 'ui.cad.props.sectionSummary',
            rows: [{
                id: 'summary.count',
                labelKey: 'ui.cad.props.selectedCount',
                value: { kind: 'readOnly', text: String(entities.length) },
            }],
        }];
    }

    const sections: PropSection[] = [generalSection(entities, ctx)];
    if (entities.length === 1) {
        const geo = geometrySection(entities, ctx);
        if (geo) sections.push(geo);
    }
    return sections;
}
